import type { ProjectDetailAlertDto, WorkItemStatus } from '../../../../lib/api/v2/planning'

/** Liseré tone (4px left border), per _design Round 4 §Style/tokens. */
export type SectionTone = 'gris' | 'ambre' | 'rouge' | 'vert'

export interface DerivedSectionStatus {
  /** User-first label, or `null` for the calm/factual state (no word, just the factual line). */
  label: string | null
  tone: SectionTone
  /** Number of alerts behind an "À vérifier"/"À régler" status. */
  count?: number
}

interface DeriveInput {
  status: WorkItemStatus
  /** Alerts already filtered to THIS section. */
  alerts: ProjectDetailAlertDto[]
  /** readyCheck.ready for this work item. */
  ready: boolean
  /** readyCheck.missing for this work item — used to tell « à planifier » (actionable) from « en attente » (blocked). */
  missing: string[]
  /** Whether the work item has a live RDV (currentEventId != null) — the « planifié » signal (mini-scope 28). */
  hasEvent: boolean
  /** Whether the live RDV's end is strictly in the past — flips the section to the « clôture » phase. */
  eventPast?: boolean
  /** Whether a report is already recorded — once true the item is Completed (no « À clôturer »). */
  hasReport?: boolean
  /**
   * SOLY-PLAN-006 — un créneau VIVANT dont le dernier jour travaillé n'est pas encore passé.
   *
   * N'existe que pour le métré, et c'est la décision D3 qui le rend possible : un métré rapporté
   * accepte un passage de plus, donc `Completed` a cessé de vouloir dire « c'est fini » pour vouloir
   * dire « un rapport a été saisi ». Le badge, lui, était resté sur l'ancien sens.
   */
  hasUpcomingSlot?: boolean
  /**
   * SOLY-PLAN-003 MS-09 — the pose holds créneaux, but fewer hours than its budget. Between « à
   * planifier » (nothing placed) and « planifiée » (done), and NEITHER of those two words is true:
   * saying « planifiée » of a site with a day still to book is what let scénario S4 — the site
   * taken apart, waiting for the electrician — leave the cockpit unnoticed.
   */
  partiallyPlanned?: boolean
  kind: 'survey' | 'installation'
}

/**
 * Derive the user-first section status. Never surfaces the raw lifecycle — maps it to what the user
 * needs, in strict priority order (mini-scope 28):
 *
 *   NotApplicable → Completed → InProgress(pose) → hard → soft → Open
 *
 * Within `Open`, the planning state is read from the live-RDV ref (`hasEvent`), not the lifecycle:
 *   - no RDV  → « À planifier » (ambre, actionable) — or « En attente » (gris) when a pose is still
 *               blocked upstream (métré/commandes manquants) : « pas encore possible » ≠ « à planifier ».
 *   - has RDV → « Prêt » (vert) when the ready-check passes, otherwise « Planifié » (gris).
 *
 * Pure: no side effects, fully testable. Alerts MUST be pre-filtered to this section by the caller.
 */
export function deriveSectionStatus({ status, alerts, ready, missing, hasEvent, eventPast = false, hasReport = false, partiallyPlanned = false, hasUpcomingSlot = false, kind }: DeriveInput): DerivedSectionStatus {
  if (status === 'NotApplicable') {
    return { label: kind === 'survey' ? 'Pas de métré' : 'Pas de pose', tone: 'gris' }
  }
  // 🔴 SOLY-PLAN-006 — « Effectué » ne court-circuite QUE s'il ne reste aucun passage à venir.
  //
  // Ce retour est deuxième dans la priorité, donc il saute tout ce qui suit — y compris le
  // classement des alertes. Sur un métré rapporté qui repasse jeudi, ça produisait deux mensonges :
  // le Suivi affichait « aucun métreur assigné » en rouge pendant que le tiroir, qu'on atteint EN
  // CLIQUANT sur ce point, affichait « Effectué » en vert sans compteur ; et hors fenêtre de
  // préavis, où aucune alerte ne part, le badge annonçait « Effectué » d'un métré où quelqu'un doit
  // encore se déplacer.
  //
  // Depuis D3, `Completed` veut dire « un rapport a été saisi », plus « le travail est fini ». Avec
  // un passage à venir, le métré retombe donc dans le classement normal : alertes d'abord, puis
  // « Prêt » / « Planifié » selon le ready-check — qui lit TOUS les créneaux depuis MS-03, donc un
  // 2ᵉ passage sans métreur ne peut pas rendre « Prêt ».
  //
  // Le cas dominant — un métré rapporté, plus rien devant — dit « Effectué » exactement comme avant.
  if (status === 'Completed' && !hasUpcomingSlot) {
    return { label: kind === 'survey' ? 'Effectué' : 'Effectuée', tone: 'vert' }
  }
  // Phase « clôture » (mini-scope 29): RDV passé, pas encore de rapport → la seule action restante est
  // « terminer + rapport ». Prioritaire sur « En cours » et sur les alertes (la lead-time #2/#3/#10/#11
  // s'est déjà éteinte ; le rapport-manquant #4/#13 porte la même info que ce badge).
  if ((status === 'Open' || status === 'InProgress') && eventPast && !hasReport) {
    return { label: 'À clôturer', tone: 'ambre' }
  }
  if (status === 'InProgress' && kind === 'installation') {
    return { label: 'En cours', tone: 'vert' }
  }

  const hasHard = alerts.some((a) => a.severity === 'Hard')
  const hasSoft = alerts.some((a) => a.severity === 'Soft')
  if (hasHard) return { label: 'À régler', tone: 'rouge', count: alerts.length }
  if (hasSoft) return { label: 'À vérifier', tone: 'ambre', count: alerts.length }

  // `Completed` + un passage à venir emprunte la même sortie que `Open` : la question n'est plus
  // « le travail est-il fait », elle est « le prochain passage est-il prêt ».
  if (status === 'Open' || (status === 'Completed' && hasUpcomingSlot)) {
    if (!hasEvent) {
      // A pose can't be planned until its métré is done and its goods are in — that's « en attente »,
      // not an action the user is sitting on. Surveys have no upstream, so they're always « à planifier ».
      const blocked = kind === 'installation' && (missing.includes('survey') || missing.includes('supplierOrders'))
      return blocked ? { label: 'En attente', tone: 'gris' } : { label: 'À planifier', tone: 'ambre' }
    }
    // Placed, but not for the whole budget: actionable, and it must NOT read as done (MS-09).
    // Ranked below the alert checks above on purpose — a hard point to settle comes first.
    if (partiallyPlanned) return { label: 'Partiellement planifiée', tone: 'ambre' }
    // Has a live RDV → « planifié » (C2: never blank). « Prêt » once the ready-check passes.
    return ready ? { label: 'Prêt', tone: 'vert' } : { label: kind === 'survey' ? 'Planifié' : 'Planifiée', tone: 'gris' }
  }

  return { label: null, tone: 'gris' } // Cancelled (terminal line shown by the section)
}
